/**
 * Configuración de i18next para toda la aplicación.
 *
 * Los recursos se empaquetan en el bundle (son pocos y evita parpadeos de
 * texto); el idioma inicial es el de por defecto y `LanguageSync` lo ajusta
 * según la URL. `useSuspense: false` porque no hay carga asíncrona.
 * Se importa una sola vez desde `index.js` (y `setupTests.js` en los tests).
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from './languages';
import { NAMESPACES } from './namespaces';
import esCommon from '../locales/es/common.json';
import enCommon from '../locales/en/common.json';
import frCommon from '../locales/fr/common.json';
import ptCommon from '../locales/pt/common.json';
import esForms from '../locales/es/forms.json';
import enForms from '../locales/en/forms.json';
import frForms from '../locales/fr/forms.json';
import ptForms from '../locales/pt/forms.json';
import esContact from '../locales/es/contact.json';
import enContact from '../locales/en/contact.json';
import frContact from '../locales/fr/contact.json';
import ptContact from '../locales/pt/contact.json';
import esHome from '../locales/es/home.json';
import enHome from '../locales/en/home.json';
import frHome from '../locales/fr/home.json';
import ptHome from '../locales/pt/home.json';
import esAbout from '../locales/es/about.json';
import enAbout from '../locales/en/about.json';
import frAbout from '../locales/fr/about.json';
import ptAbout from '../locales/pt/about.json';
import esProducts from '../locales/es/products.json';
import enProducts from '../locales/en/products.json';
import frProducts from '../locales/fr/products.json';
import ptProducts from '../locales/pt/products.json';
import esRecipes from '../locales/es/recipes.json';
import enRecipes from '../locales/en/recipes.json';
import frRecipes from '../locales/fr/recipes.json';
import ptRecipes from '../locales/pt/recipes.json';
import esNews from '../locales/es/news.json';
import enNews from '../locales/en/news.json';
import frNews from '../locales/fr/news.json';
import ptNews from '../locales/pt/news.json';
import esLegal from '../locales/es/legal.json';
import enLegal from '../locales/en/legal.json';
import frLegal from '../locales/fr/legal.json';
import ptLegal from '../locales/pt/legal.json';
import esPrivacy from '../locales/es/privacy.json';
import enPrivacy from '../locales/en/privacy.json';
import frPrivacy from '../locales/fr/privacy.json';
import ptPrivacy from '../locales/pt/privacy.json';
import esTransparency from '../locales/es/transparency.json';
import enTransparency from '../locales/en/transparency.json';
import frTransparency from '../locales/fr/transparency.json';
import ptTransparency from '../locales/pt/transparency.json';
import esTransparencySubsidies from '../locales/es/transparencySubsidies.json';
import enTransparencySubsidies from '../locales/en/transparencySubsidies.json';
import frTransparencySubsidies from '../locales/fr/transparencySubsidies.json';
import ptTransparencySubsidies from '../locales/pt/transparencySubsidies.json';
import esTransparencyDirectors from '../locales/es/transparencyDirectors.json';
import enTransparencyDirectors from '../locales/en/transparencyDirectors.json';
import frTransparencyDirectors from '../locales/fr/transparencyDirectors.json';
import ptTransparencyDirectors from '../locales/pt/transparencyDirectors.json';
import esTransparencyRules from '../locales/es/transparencyRules.json';
import enTransparencyRules from '../locales/en/transparencyRules.json';
import frTransparencyRules from '../locales/fr/transparencyRules.json';
import ptTransparencyRules from '../locales/pt/transparencyRules.json';
import esSeo from '../locales/es/seo.json';
import enSeo from '../locales/en/seo.json';
import frSeo from '../locales/fr/seo.json';
import ptSeo from '../locales/pt/seo.json';

export { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, LANGUAGE_LABELS } from './languages';

/** Espacio de nombres por defecto (elementos compartidos). */
export const DEFAULT_NAMESPACE = 'common';

i18n.use(initReactI18next).init({
  resources: {
    es: { common: esCommon, forms: esForms, contact: esContact, home: esHome, about: esAbout, products: esProducts, recipes: esRecipes, news: esNews, legal: esLegal, privacy: esPrivacy, transparency: esTransparency, transparencySubsidies: esTransparencySubsidies, transparencyDirectors: esTransparencyDirectors, transparencyRules: esTransparencyRules, seo: esSeo },
    en: { common: enCommon, forms: enForms, contact: enContact, home: enHome, about: enAbout, products: enProducts, recipes: enRecipes, news: enNews, legal: enLegal, privacy: enPrivacy, transparency: enTransparency, transparencySubsidies: enTransparencySubsidies, transparencyDirectors: enTransparencyDirectors, transparencyRules: enTransparencyRules, seo: enSeo },
    fr: { common: frCommon, forms: frForms, contact: frContact, home: frHome, about: frAbout, products: frProducts, recipes: frRecipes, news: frNews, legal: frLegal, privacy: frPrivacy, transparency: frTransparency, transparencySubsidies: frTransparencySubsidies, transparencyDirectors: frTransparencyDirectors, transparencyRules: frTransparencyRules, seo: frSeo },
    pt: { common: ptCommon, forms: ptForms, contact: ptContact, home: ptHome, about: ptAbout, products: ptProducts, recipes: ptRecipes, news: ptNews, legal: ptLegal, privacy: ptPrivacy, transparency: ptTransparency, transparencySubsidies: ptTransparencySubsidies, transparencyDirectors: ptTransparencyDirectors, transparencyRules: ptTransparencyRules, seo: ptSeo },
  },
  lng: DEFAULT_LANGUAGE,
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: SUPPORTED_LANGUAGES,
  defaultNS: DEFAULT_NAMESPACE,
  ns: NAMESPACES,
  interpolation: { escapeValue: false }, // React ya escapa el contenido
  react: { useSuspense: false },
});

export default i18n;
